import { useEffect, useRef, useState } from "react";
import styles from "./Dashboard.module.scss";

import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import Grow from "@material-ui/core/Grow";
import Input from "@material-ui/core/Input";
import MenuItem from "@material-ui/core/MenuItem";
import MenuList from "@material-ui/core/MenuList";
import Paper from "@material-ui/core/Paper";
import Popper from "@material-ui/core/Popper";
import Select from "@material-ui/core/Select";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";

import CoachingHours from "../../components/CoachingHours";
import CoachingSubject from "../../components/CoachingSubject";
import ComplianceRate from "../../components/CompianceRate";
import SessionClosed from "../../components/SessionClosed";
import { apiRequest } from "../../utils/api";

import { useTranslation } from "react-i18next";
import { getLocalValue } from "../../utils/auth";
import { downloadCSV } from "../../utils/downloadCSV";
import getUserIP, { formatIPJSON } from "../../utils/getUserIP";

function CEODashboard() {
  const {
    Parser,
    transforms: { unwind },
  } = require("json2csv");
  const { t } = useTranslation();
  const uid = getLocalValue("uid");
  const filterBy = ["monthly", "quarterly", "yearly"];
  const [filterValue, setFilterValue] = useState(["monthly"]);
  const [sessionClosed, setSessionClosed] = useState(null);
  const [coachingSubjectData, setCoachingSubjectData] = useState(null);
  const [complianceMetricsValue, setComplianceMetricsValue] = useState(null);
  const [ceoCoachingHours, setCeoCoachingHours] = useState(null);
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const [userIP, setUserIP] = useState(null);

  useEffect(() => {
    getUserIP().then(res => setUserIP(formatIPJSON(res)));
  }, []);

  useEffect(() => {
    getCEOOverallSessionClosed();
    getCeoCoachedSubjectsMetrics();
    getCeoDashboardMetrics();
    getCeoCoachingHours();
  }, [filterValue]);

  const getCEOOverallSessionClosed = () => {
    apiRequest
      .get(`/api/getCEOOverallSessionClosed?uid=${uid}&filter=${filterValue}`)
      .then(response => {
        if (response.status === 200) {
          const data = response.data.data;
          setSessionClosed(data);
        }
      })
      .catch(error => { });
  };

  const getCeoCoachedSubjectsMetrics = () => {
    apiRequest
      .get(`/api/getCeoCoachedSubjectsMetrics?uid=${uid}&filter=${filterValue}`)
      .then(response => {
        if (response.status === 200) {
          const data = response.data.data;
          setCoachingSubjectData(data);
        }
      })
      .catch(error => { });
  };

  const getCeoDashboardMetrics = () => {
    apiRequest
      .get(`/api/getCeoDashboardMetrics?uid=${uid}&filter=${filterValue}`)
      .then(response => {
        if (response.status === 200) {
          const data = response.data.data;

          setComplianceMetricsValue(data);
        }
      })
      .catch(error => { });
  };

  const getCeoCoachingHours = () => {
    apiRequest
      .get(`/api/getCeoCoachingHours?uid=${uid}&filter=${filterValue}`)
      .then(response => {
        if (response.status === 200) {
          const data = response.data.data;
          setCeoCoachingHours(data);
        }
      })
      .catch(error => { });
  };

  const getReport = () => {
    apiRequest
      .get(
        `/api/getOverallCoachingSessions?filter=${filterValue}&userId=${uid}&userIp=${userIP}`
      )
      .then(response => {
        if (response.status === 200) {
          let inputJSON = response.data.data.overallCoachingSession;

          inputJSON.forEach((item, index) => {
            if (item.session_subjects.length > 0) {
              item.session_subjects.forEach((subject, subindex) => {
                if (subject.session_checkpoints.length === 0) {
                  inputJSON[index].session_subjects[
                    subindex
                  ].session_checkpoints = [{}];
                }
              });
            }
            if (item.session_review.length === 0) {
              inputJSON[index].session_review = [{}];
            }
            if (item.session_subjects.length === 0) {
              inputJSON[index].session_subjects = [{}];
            }
          });

          const fields = [
            "session_id",
            "session_created_date",
            "country",
            "campaign_name",
            "is_closed",
            "acknowledged_time",
            "act_timing",
            "actual_coaching_date",
            "archive_session_timestamp",
            "check_timing",
            "coach",
            "coachee",
            "coachee_behaviour_rating",
            "coachee_satisfaction",
            "coaching_date",
            "do_timing",
            "goals",
            "method",
            "options",
            "plan_timing",
            "review_date_current",
            "review_time_current",
            "session_review.review_date",
            "session_review.review_time",
            "session_subjects.subject_id",
            "session_subjects.current_score",
            "session_subjects.main_subject_name",
            "session_subjects.sub_subject_name",
            "session_subjects.skill_to_improve",
            "session_subjects.session_checkpoints.session_id",
            "session_subjects.session_checkpoints.subject_id",
            "session_subjects.session_checkpoints.checkpoint_date",
            "session_subjects.session_checkpoints.checkpoint_score",
            "summary",
            "total_duration",
            "type_of_session",
            "way_forward",
          ];

          const transforms = [
            unwind({
              paths: [
                "session_review",
                "session_subjects",
                "session_subjects.session_checkpoints",
              ],
              unwindBlank: true,
              blankOut: true,
            }),
          ];
          try {
            const parser = new Parser({ fields, transforms });
            const CSV = parser.parse(inputJSON);

            //CSV string and filename
            downloadCSV(CSV, "Overall Coaching Sessions");

            handleClose();
          } catch (err) {
            console.error(err);
          }
        }
      })
      .catch(error => { });
  };

  const getCoachingDiscussionReport = () => {
    apiRequest
      .get(
        `/api/getCoachingSessionDiscussion?filter=${filterValue}&userId=${uid}&userIp=${userIP}`
      )
      .then(response => {
        if (response.status === 200) {
          const inputJSON = response.data.data.coachingSessionDiscussion;
          const fields = [
            "session_id",
            "date_of_review",
            "coach",
            "coachee",
            "casual_notes",
            "casual_notes_given_by",
            "compliments",
            "compliment_given_by",
            "compliment_liked_flag_coach",
            "compliment_liked_flag_coachee",
            "key_points",
            "key_points_given_by",
          ];

          // const transforms = [unwind({ paths: ['session_review', 'session_subjects', 'session_subjects.session_checkpoints'], blankOut: true })];
          try {
            const parser = new Parser({ fields });
            const CSV = parser.parse(inputJSON);

            //CSV string and filename
            downloadCSV(CSV, "Session discussion report");
            handleClose();
          } catch (err) {
            console.error(err);
          }
        }
      })
      .catch(error => { });
  };

  const handleChange = event => {
    setFilterValue(event.target.value);
  };

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen);
  };

  const handleClose = event => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setOpen(false);
  };

  function handleListKeyDown(event) {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpen(false);
    }
  }

  return (
    <div className={styles.detailsContainer}>
      <div className={styles.toolBar}>
        <span style={{ paddingRight: "10px" }}>
          {t("dashboard.coach.showResultFor")}
        </span>
        <Select
          className={styles.sectionFilter}
          value={filterValue}
          onChange={handleChange}
          input={<Input />}
          IconComponent={ExpandMoreIcon}
        >
          {filterBy.map(filter => (
            <MenuItem key={filter} value={filter}>
              {filter}
            </MenuItem>
          ))}
        </Select>
      </div>
      {/* <button
          className={styles.downloadButton}
          // onClick={()=>{
          //   getCoachingDiscussionReport();
          //   getReport();
          // }}
          ref={anchorRef}
          aria-controls={open ? "menu-list-grow" : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
        >
          <GetAppOutlinedIcon fontSize="small" />
          Report
        </button> */}
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom" ? "center top" : "center bottom",
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  autoFocusItem={open}
                  id="menu-list-grow"
                  onKeyDown={handleListKeyDown}
                >
                  <MenuItem onClick={getReport}>
                    {t("dashboard.overallCoachingSessionsReport")}{" "}
                  </MenuItem>
                  <MenuItem onClick={getCoachingDiscussionReport}>
                    {t("dashboard.sessionDiscussionReport")}
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>

      <SessionClosed
        maxHeight="250px"
        labels={[
          t("userManagement.country"),
          t("dashboard.executive.totalInprogress"),
          t("dashboard.executive.totalSessionClosed"),
          t("dashboard.executive.totalCoachees"),
          t("dashboard.executive.avgSessionClosed"),
          t("dashboard.executive.usageRate"),
        ]}
        country={true}
        data={sessionClosed}
      />

      <CoachingHours country={true} data={ceoCoachingHours} />

      {coachingSubjectData && (
        <CoachingSubject data={coachingSubjectData} country={true} />
      )}

      {complianceMetricsValue ? (
        <ComplianceRate
          data={complianceMetricsValue}
          country={true}
          labelX={t("userManagement.country")}
        />
      ) : (
        <div>{t("dashboard.noDataYet")}</div>
      )}
    </div>
  );
}

export default CEODashboard;
